import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Empty, ErrorState, Loading, useToast } from '../../components/ui';
import { api } from '../../lib/api';
import { dateTime } from '../../lib/format';

interface Grant {
  id: string;
  tenantId?: string;
  tenantName?: string | null;
  propertyId: string | null;
  reason: string;
  ticketReference: string;
  startedAt: string;
  expiresAt: string;
  endedAt: string | null;
  staffName: string | null;
  active: boolean;
}

function GrantState({ grant }: { grant: Grant }) {
  if (grant.active) return <span className="chip ok">Open until {dateTime(grant.expiresAt)}</span>;
  return <span className="chip">{grant.endedAt ? `Ended ${dateTime(grant.endedAt)}` : `Expired ${dateTime(grant.expiresAt)}`}</span>;
}

/**
 * The platform console's list of support access windows: a support user's
 * own, or everyone's for a superadmin. Opening one happens where it is needed
 * - on the account itself.
 */
export function PlatformSupportAccessTab() {
  const { push, fail } = useToast();
  const queryClient = useQueryClient();
  const [activeOnly, setActiveOnly] = useState(true);
  const grants = useQuery({
    queryKey: ['platform-support-access', activeOnly],
    queryFn: () => api.get<Grant[]>(`/api/platform/privacy/support-access?active=${activeOnly}`),
  });
  const end = useMutation({
    mutationFn: (id: string) => api.post(`/api/platform/privacy/support-access/${id}/end`),
    onSuccess: () => {
      push('Support access ended', 'ok');
      void queryClient.invalidateQueries({ queryKey: ['platform-support-access'] });
    },
    onError: fail,
  });

  if (grants.isLoading) return <Loading />;
  if (grants.error) return <ErrorState error={grants.error} onRetry={() => void grants.refetch()} />;
  const rows = grants.data ?? [];
  return (
    <div className="card">
      <div className="card-head">
        <h3>Support access</h3>
        <label className="checkbox">
          <input type="checkbox" checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />
          Open only
        </label>
      </div>
      <div className="card-body small muted">
        Support staff open a window onto one account - with a reason and a ticket - before they can read it. The
        hotel sees each window and everything opened under it. Close a window when the work is done.
      </div>
      {rows.length === 0 ? (
        <Empty message={activeOnly ? 'No support access open' : 'No support access recorded'} />
      ) : (
        <div className="table-wrap">
          <table className="tbl tbl-stack">
            <thead>
              <tr>
                <th>Account</th>
                <th>Staff</th>
                <th>Why</th>
                <th>Ticket</th>
                <th>State</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((grant) => (
                <tr key={grant.id}>
                  <td className="b">{grant.tenantName ?? grant.tenantId?.slice(0, 8)}</td>
                  <td>{grant.staffName}</td>
                  <td className="small">{grant.reason}</td>
                  <td className="mono xs">{grant.ticketReference}</td>
                  <td>
                    <GrantState grant={grant} />
                  </td>
                  <td>
                    {grant.active ? (
                      <button className="btn sm" disabled={end.isPending} onClick={() => end.mutate(grant.id)}>
                        End
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

interface HotelView {
  grants: Grant[];
  accesses: Array<{ at: string; staffName: string | null; resource: string | null; grantId: string | null; administrator: boolean }>;
}

/** The hotel's view: when SARTHI staff looked into this account, why, and at what. */
export function HotelSupportAccessTab() {
  const view = useQuery({
    queryKey: ['privacy-support-access'],
    queryFn: () => api.get<HotelView>('/api/privacy/support-access'),
  });
  if (view.isLoading) return <Loading />;
  if (view.error) return <ErrorState error={view.error} onRetry={() => void view.refetch()} />;
  const { grants, accesses } = view.data!;
  return (
    <div className="stack">
      <div className="card">
        <div className="card-head">
          <h3>When SARTHI support opened your account</h3>
        </div>
        <div className="card-body small muted">
          SARTHI support staff must say why, and give a ticket reference, before they can look at your account,
          and their access ends on its own. SARTHI administrators can look without opening a window; what they
          looked at is listed below all the same.
        </div>
        {grants.length === 0 ? (
          <Empty message="SARTHI support has not opened your account" />
        ) : (
          <div className="table-wrap">
            <table className="tbl tbl-stack">
              <thead>
                <tr>
                  <th>Opened</th>
                  <th>By</th>
                  <th>Why</th>
                  <th>Ticket</th>
                  <th>State</th>
                </tr>
              </thead>
              <tbody>
                {grants.map((grant) => (
                  <tr key={grant.id}>
                    <td>{dateTime(grant.startedAt)}</td>
                    <td>{grant.staffName}</td>
                    <td className="small">{grant.reason}</td>
                    <td className="mono xs">{grant.ticketReference}</td>
                    <td>
                      <GrantState grant={grant} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className="card">
        <div className="card-head">
          <h3>What was looked at</h3>
        </div>
        {accesses.length === 0 ? (
          <Empty message="Nothing yet" />
        ) : (
          <div className="table-wrap">
            <table className="tbl tbl-stack">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Who</th>
                  <th>What</th>
                </tr>
              </thead>
              <tbody>
                {accesses.map((access, index) => (
                  <tr key={`${access.at}-${index}`}>
                    <td>{dateTime(access.at)}</td>
                    <td>
                      {access.staffName}
                      {access.administrator ? <span className="xs muted"> (administrator)</span> : null}
                    </td>
                    <td>{access.resource}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
